class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

const canHover = window.matchMedia('(hover: hover)').matches;

class HeaderMenu extends HTMLElement {
  constructor() {
    super();

    this.link = this.querySelector('.js-header__menu-item');
    this.anchorWithSublinks = this.querySelector('[data-has-sublinks]');
    this.contentId = this.dataset.hoverMenuToggle;
    this.content = document.querySelector(`[data-hover-menu-content="${this.contentId}"]`);
    this.drawerToggle = document.querySelector(`[data-drawer-menu-toggle="${this.contentId}"]`);
    this.megaMenuWrapper = document.querySelector(`.mega-menu__section-wrapper`);
    this.navOverlay = document.querySelector(`.nav-overlay`);
    this.hoverTimer = null;
    this.waitForMenuAnimationEnd = null;
    this.closeButton = this.content?.querySelector('.mega-menu__close-section a');
    this.allFocusableElements = this.content
      ? Array.from(this.content.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      : [];
    this.translations = document.querySelector('[data-accessibility-translations]')
      ? JSON.parse(document.querySelector('[data-accessibility-translations]').textContent)
      : {};

    // Set initial tabindex for all focusable elements
    this.setTabIndexForMenuItems(-1);

    // Set up ARIA attributes for accessibility
    if (this.content) {
      // Ensure the submenu has an ID for aria-controls
      if (!this.content.id) {
        this.content.id = `submenu-${this.contentId}`;
      }

      // Set up trigger link attributes
      this.link.setAttribute('aria-haspopup', 'true');
      this.link.setAttribute('aria-expanded', 'false');
      this.link.setAttribute('aria-controls', this.content.id);
    }

    // Set aria label for screen readers
    const checkIsHoverEnabled = () => window.matchMedia('(min-width: 1024px)').matches;

    let removeHoverEventsListeners = null;
    if (checkIsHoverEnabled()) {
      removeHoverEventsListeners = this.bindHoverEvents();
    }

    window.addEventListener('resize', () => {
      if (checkIsHoverEnabled()) {
        if (removeHoverEventsListeners === null) {
          removeHoverEventsListeners = this.bindHoverEvents();
        }
      } else {
        if (removeHoverEventsListeners) {
          removeHoverEventsListeners();
          removeHoverEventsListeners = null;
        }
      }
    });

    if (this.closeButton) {
      this.bindCloseEvents();
    }

    this.bindKeyboardEvents();
    this.bindSubnavEvents();
    this.resetExpandedSubnav();
  }

  get menuItems() {
    return this.content ? Array.from(this.content.querySelectorAll('.js-focus')).filter((el) => !el.hidden) : [];
  }

  setTabIndexForMenuItems(value) {
    this.allFocusableElements.forEach((element) => {
      element.setAttribute('tabindex', value);
    });
  }

  bindKeyboardEvents() {
    // Add focusout event listener to close menu when focus moves outside
    if (this.content) {
      this.content.addEventListener('focusout', (event) => {
        const nextFocus = event.relatedTarget;

        if (!nextFocus) return;

        if (this.content.contains(nextFocus) || this.link.contains(nextFocus)) {
          return;
        }

        this.close();
      });
    }

    if (!this.link) {
      return;
    }

    // Add focusout event listener to the trigger link
    this.link.addEventListener('focusout', (event) => {
      // If focus is moving outside both the link and the content
      if (!this.content?.contains(event.relatedTarget) && event.relatedTarget !== this.content) {
        this.close();
      }
    });

    this.link.addEventListener('click', (event) => {
      if (this.content) {
        event.stopPropagation();
        event.preventDefault();

        if (!this.hasAttribute('open')) {
          this.openMenuWithoutFocus();
          this.focusSubMenu();
        } else {
          this.close();
        }
      }
    });

    this.anchorWithSublinks?.addEventListener('click', (event) => {
      const hasSublinks = this.anchorWithSublinks.dataset.hasSublinks === 'true';

      if (hasSublinks) {
        event.preventDefault();
        this.openMenuWithoutFocus();
        this.focusSubMenu();
      }
    });

    this.link.addEventListener('keydown', (event) => {
      switch (event.key) {
        case ' ': // Space
        case 'Enter':
          event.preventDefault();
          if (this.content) {
            if (!this.hasAttribute('open')) {
              this.openMenuWithoutFocus();
              this.focusSubMenu();
            } else {
              this.close();
            }
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'Home':
          event.preventDefault();
          if (this.menuItems.length) {
            this.menuItems[0].focus();
          }
          break;
        case 'End':
          event.preventDefault();
          if (this.menuItems.length) {
            this.menuItems[this.menuItems.length - 1].focus();
          }
          break;
      }
    });

    if (this.content) {
      this.content.addEventListener('keydown', (event) => {
        const currentIndex = this.menuItems.indexOf(document.activeElement);

        switch (event.key) {
          case 'Escape':
            event.preventDefault();
            this.close();
            this.link.focus();
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (currentIndex >= 0) {
              const nextIndex = currentIndex === this.menuItems.length - 1 ? 0 : currentIndex + 1;
              this.menuItems[nextIndex].focus();
            }
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (currentIndex >= 0) {
              const prevIndex = currentIndex === 0 ? this.menuItems.length - 1 : currentIndex - 1;
              this.menuItems[prevIndex].focus();
            }
            break;
          case 'Home':
            event.preventDefault();
            if (this.menuItems.length) {
              this.menuItems[0].focus();
            }
            break;
          case 'End':
            event.preventDefault();
            if (this.menuItems.length) {
              this.menuItems[this.menuItems.length - 1].focus();
            }
            break;
          case 'Tab':
            event.preventDefault();
            if (currentIndex >= 0) {
              if (event.shiftKey) {
                if (currentIndex === 0) {
                  this.link.focus();
                } else {
                  const prevIndex = currentIndex - 1;
                  this.menuItems[prevIndex].focus();
                }
              } else {
                if (currentIndex === this.menuItems.length - 1) {
                  const parentLi = this.closest('li');
                  const nextLi = parentLi?.nextElementSibling;
                  if (nextLi) {
                    const nextHeaderMenu = nextLi.querySelector('header-menu');
                    const nextLink = nextHeaderMenu?.querySelector('.js-header__menu-item');
                    if (nextLink) {
                      nextLink.focus();
                    } else {
                      this.link.focus();
                    }
                  } else {
                    this.link.focus();
                  }
                } else {
                  const nextIndex = currentIndex + 1;
                  this.menuItems[nextIndex].focus();
                }
              }
            }
            break;
        }
      });
    }
  }

  focusSubMenu() {
    if (this.content && this.menuItems.length > 0) {
      this.menuItems[0].focus();
    }
  }

  bindHoverEvents() {
    if (!window.currentOpenMenuContent) {
      window.currentOpenMenuContent = null;
    }

    const handleMouseEnter = () => {
      this.waitForMenuAnimationEnd?.cancel();
      clearTimeout(this.hoverTimer);

      window.currentOpenMenuContent = this.content;

      // If this menu has content, open it
      if (this.content && !this.hasAttribute('open')) {
        this.openMenuWithoutFocus();
      } else if (!this.content) {
        // If this menu doesn't have content, close all open menus
        this.closeAllMenus();
        window.currentOpenMenuContent = null;
      }
    };

    const handleMouseLeave = (event) => {
      const relatedTarget = event.relatedTarget;

      // Skip if there's no related target
      if (!relatedTarget) return;

      if (
        this.content &&
        (relatedTarget === this.content ||
          this.content.contains(relatedTarget) ||
          relatedTarget === this ||
          this.contains(relatedTarget))
      ) {
        return;
      }

      this.hoverTimer = setTimeout(() => {
        const isOverMenuOrContent = this.matches(':hover') || (this.content && this.content.matches(':hover'));

        if (relatedTarget.hasAttribute('data-mega-toggle')) {
          return this.close();
        }

        if (relatedTarget.classList.contains('mega-menu__section-wrapper')) {
          return this.closeAllMenus();
        }

        if (isOverMenuOrContent) {
          this.waitForMenuAnimationEnd?.cancel();
          return;
        }

        if (this.content) {
          this.waitForMenuAnimationEnd?.cancel();
          this.waitForMenuAnimationEnd = waitForAnimationEnd(document.querySelector('.mega-menu__section-wrapper'));

          this.waitForMenuAnimationEnd.promise.then((result) => {
            if (result.canceled) {
              return;
            }

            const targetRect = this.content.getBoundingClientRect();
            const withinBound =
              targetRect.top + 1 <= event.clientY &&
              event.clientY <= targetRect.bottom - 1 &&
              targetRect.left + 1 <= event.clientX &&
              event.clientX <= targetRect.right - 1;

            if (!withinBound) {
              this.close();
            }
          });
        } else {
          if (!isOverMenuOrContent) {
            this.close();
          }
        }
      }, 150);
    };

    const handleContentMouseEnter = () => {
      this.waitForMenuAnimationEnd?.cancel();
      clearTimeout(this.hoverTimer);
      window.currentOpenMenuContent = this.content;

      if (!this.hasAttribute('open')) {
        this.setAttribute('open', 'open');
        this.link.setAttribute('aria-expanded', 'true');
        this.content.setAttribute('aria-expanded', 'true');
      }
    };

    this.addEventListener('mouseenter', handleMouseEnter);
    this.addEventListener('mouseleave', handleMouseLeave);

    // Content event listeners
    if (this.content) {
      this.content.addEventListener('mouseenter', handleContentMouseEnter);
      this.content.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      this.removeEventListener('mouseenter', handleMouseEnter);
      this.removeEventListener('mouseleave', handleMouseLeave);

      if (this.content) {
        this.content.removeEventListener('mouseenter', handleContentMouseEnter);
        this.content.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }

  handleMouseClick(event) {
    event.preventDefault();

    if (!this.hasAttribute('open')) {
      this.openMenuWithoutFocus();
    }
  }

  bindClickEvents() {
    this.addEventListener('click', this.handleMouseClick);
  }

  bindCloseEvents() {
    this.closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.closeDrawerMenu();
    });
  }

  openMenuWithoutFocus() {
    this.waitForMenuAnimationEnd?.cancel();
    if (this.content) {
      if (window.currentOpenMenuContent && window.currentOpenMenuContent !== this.content) {
        const allMenus = document.querySelectorAll('header-menu[open]');
        allMenus.forEach((menu) => {
          if (menu !== this) {
            menu.closeMenuOnly();
          }
        });
      }

      this.setAttribute('open', 'open');
      this.megaMenuWrapper.setAttribute('open', 'open');
      this.navOverlay.setAttribute('aria-hidden', 'false');
      this.link.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('data-staged', 'true');

      const topLink = this?.anchorWithSublinks?.attributes?.['data-top-link']?.value;
      this.setFocusedParentLink(topLink, 'data-top-link');

      // Enable tabbing through menu items
      this.setTabIndexForMenuItems(0);

      // Stage other menus
      const otherMenuContents = Array.from(document.querySelectorAll('[data-hover-menu-content]')).filter(
        (el) => el !== this.content
      );

      otherMenuContents.forEach((el) => {
        el.setAttribute('data-staged', 'true');
      });

      window.currentOpenMenuContent = this.content;
    }
  }

  closeMenuOnly() {
    if (this.content) {
      this.removeAttribute('open');
      this.drawerToggle.removeAttribute('open');
      this.link.setAttribute('aria-expanded', 'false');
      this.content.setAttribute('aria-expanded', 'false');

      // Disable tabbing through menu items
      this.setTabIndexForMenuItems(-1);
    }
  }

  close() {
    this.closeMenuOnly();
    this.resetAllParentLinks('data-top-link');

    const openMenus = document.querySelectorAll('header-menu[open]');
    if (openMenus.length === 0) {
      this.navOverlay.setAttribute('aria-hidden', 'true');

      // Unstage all menus
      const otherMenuContents = Array.from(document.querySelectorAll('[data-hover-menu-content]'));
      otherMenuContents.forEach((el) => {
        el.setAttribute('data-staged', 'false');
      });

      this.megaMenuWrapper.removeAttribute('open');
    }
  }

  // Close all menus completely
  closeAllMenus() {
    const allOpenMenus = document.querySelectorAll('header-menu[open]');
    allOpenMenus.forEach((menu) => {
      menu.closeMenuOnly();
    });

    // Close the wrapper and overlay
    this.navOverlay.setAttribute('aria-hidden', 'true');

    this.megaMenuWrapper.removeAttribute('open');
  }

  closeDrawerMenu() {
    this.removeAttribute('open');
    this.drawerToggle.removeAttribute('open');
    this.megaMenuWrapper.removeAttribute('bg');
    if (this.content) {
      // Disable tabbing through menu items
      this.setTabIndexForMenuItems(-1);

      this.megaMenuWrapper.removeAttribute('open');
      this.content.setAttribute('aria-expanded', 'false');
    }
  }

  resetExpandedSubnav() {
    this.resetAllParentLinks('data-mega-link');

    const images = this.content?.querySelectorAll('.mega-menu__image-panel');

    images?.forEach((image) => {
      image.classList.remove('active');
      image.querySelectorAll('.js-focus').forEach((el) => el.setAttribute('hidden', ''));
    });

    const panels = this.content?.querySelectorAll('.mega-menu__subnav-panel');

    panels?.forEach((panel) => {
      panel.classList.remove('active');

      panel.querySelectorAll('.js-focus').forEach((el) => el.setAttribute('hidden', ''));
    });

    this.content?.querySelectorAll('[data-subnav-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.content?.querySelectorAll('[data-subnav-content]').forEach((panel) => {
      panel.classList.remove('active');
      panel.hidden = true;
    });
  }

  resetAllParentLinks(parentLinkQuery) {
    const allTopLinks = document?.querySelectorAll(`[${parentLinkQuery}]`);
    allTopLinks?.forEach((link) => {
      link.classList.remove('is-hovered');
    });
  }

  setFocusedParentLink(topLink, parentLinkQuery) {
    this.resetAllParentLinks(parentLinkQuery);

    if (topLink) {
      const topLinkElement = document.querySelector(`[${parentLinkQuery}="${topLink}"]`);
      if (topLinkElement) {
        topLinkElement.classList.add('is-hovered');
      }
    }
  }

  activateMenuSubnav(key, { topLink, shouldFocus = false } = {}) {
    this.resetExpandedSubnav();
    this.setFocusedParentLink(topLink, 'data-top-link');
    this.setFocusedParentLink(key, 'data-mega-link');

    const activePanel = this.content.querySelector(`[data-subnav-content="${key}"]`);

    activePanel?.classList.add('active');

    activePanel?.querySelectorAll('.js-focus').forEach((el) => el.removeAttribute('hidden'));

    const imagePanel = this.content.querySelector(`[data-image-content="${key}"]`);

    imagePanel?.classList.add('active');
    imagePanel?.querySelectorAll('.js-focus').forEach((el) => el.removeAttribute('hidden'));

    const activeTrigger = this.content.querySelector(`[data-subnav-trigger="${key}"]`);

    if (activeTrigger) {
      activeTrigger.setAttribute('aria-expanded', 'true');
    }

    if (this.content.id) {
      const menuButton = document.querySelector(`.header-menu-trigger[aria-controls="${this.content.id}"]`);

      if (menuButton) {
        menuButton.setAttribute('aria-expanded', 'true');
      }
    }

    // 👉 NEW: move focus into submenu
    if (shouldFocus) {
      const panel =
        this.content.querySelector(`[data-subnav-content="${key}"]`) ||
        this.content.querySelector(`[data-image-content="${key}"]`);

      if (!panel) return;

      const firstFocusable = panel.querySelector('a, button, [tabindex]:not([tabindex="-1"])');

      if (firstFocusable) {
        requestAnimationFrame(() => {
          firstFocusable.focus();
        });
      }
    }
  }

  bindSubnavEvents() {
    const triggers = this.content?.querySelectorAll('[data-subnav-trigger]');

    triggers?.forEach((button) => {
      const key = button.dataset.subnavTrigger;
      const topLink = button.dataset.parentTopLink;

      // hover behavior (desktop)
      if (canHover) {
        button.addEventListener('mouseenter', () => {
          this.activateMenuSubnav(key, { topLink: topLink });
        });
      }

      // click behavior (both desktop + touch)
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          this.resetExpandedSubnav();
          return;
        }

        this.activateMenuSubnav(key, { topLink: topLink, shouldFocus: true });
        return;
      });
    });

    this.content?.querySelectorAll('.menu-item--child-link').forEach((link) => {
      const key = link.dataset.megaLink;
      const topLink = link.dataset.parentTopLink;

      const hasSubnav = key && this.content.querySelector(`[data-subnav-content="${key}"]`);
      const hasImageContent = key && this.content.querySelector(`[data-image-content="${key}"]`);

      // hover behavior (desktop only)
      if (canHover) {
        link.addEventListener('mouseenter', () => {
          if (!hasSubnav && !hasImageContent) {
            // leaf link — clear any open subnav and highlight the top nav item
            this.resetExpandedSubnav();
            this.setFocusedParentLink(topLink, 'data-top-link');
            return;
          }
          this.activateMenuSubnav(key, { topLink: topLink });
        });
      }

      // click behavior (touch only) — only for links that open a submenu
      if (hasSubnav) {
        link.addEventListener('click', (event) => {
          if (!canHover) {
            event.preventDefault();
            this.activateMenuSubnav(key, { topLink: topLink, shouldFocus: true });
          }
        });
      }
    });
  }
}

customElements.define('header-menu', HeaderMenu);

class DrawerMenu extends HTMLElement {
  constructor() {
    super();

    this.link = this.querySelector('.menu-drawer__menu-item');
    this.contentId = this.dataset.drawerMenuToggle;
    this.content = document.querySelector(`[data-hover-menu-content="${this.contentId}"]`);
    this.checkForChildMenu();
  }

  checkForChildMenu() {
    if (this.link) {
      const navId = this.link.dataset.navId;
      if (navId) {
        const hasMegaMenu = !!document.querySelector(`[data-hover-menu-content="${navId.toLowerCase()}"]`);
        if (!hasMegaMenu) {
          this.link.setAttribute('no-child', '');
        }
      }
    }
  }
}

class DrawerMenuItem extends HTMLElement {
  constructor() {
    super();

    this.showImagePanel = (key) => {
      document
        .querySelectorAll('.menu-drawer .mega-menu__image-panel')
        .forEach((panel) => panel.classList.remove('active'));

      document.querySelector(`.menu-drawer [data-image-content="${key}"]`)?.classList.add('active');
    };

    this.addEventListener('click', () => {
      const key = this.dataset.imageTrigger;

      if (!key) return;

      this.showImagePanel(key);
    });
  }
}

document.querySelectorAll('.menu-drawer-top-level').forEach((details) => {
  details.addEventListener('toggle', () => {
    if (!details.open) return;

    document.querySelectorAll('.menu-drawer-top-level').forEach((other) => {
      if (other !== details) {
        other.removeAttribute('open');
      }
    });
  });
});

customElements.define('drawer-menu-item', DrawerMenuItem);
customElements.define('drawer-menu', DrawerMenu);
